import React, { useState, useRef } from 'react';
import { Upload, X, Loader2, Image as ImageIcon, Camera } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { resolveImageUrl } from '../lib/imageUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ImageUploader: drag & drop + file/camera input + URL fallback.
 * Props:
 *   value: current image URL/path (string)
 *   onChange: (newUrl) => void
 *   label: optional label
 *   maxSizeMB: max file size in MB (default 10)
 *   previewSize: 'sm' | 'md' | 'lg' (default 'md')
 */
export default function ImageUploader({
  value,
  onChange,
  label = "Image",
  maxSizeMB = 10,
  previewSize = 'md',
  testIdPrefix = 'image',
}) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const cameraRef = useRef(null);

  const sizeClasses = {
    sm: 'w-32 h-32',
    md: 'w-48 h-48',
    lg: 'w-64 h-64',
  }[previewSize] || 'w-48 h-48';

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Seules les images sont autorisées (JPG, PNG, WebP, GIF)');
      return;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`Image trop volumineuse (max ${maxSizeMB} Mo)`);
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/admin/upload/image`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(res.data.url);
      toast.success('Image téléchargée');
    } catch (e) {
      console.error('Upload error', e);
      toast.error(e.response?.data?.detail || 'Erreur de téléchargement');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  };

  const previewUrl = resolveImageUrl(value);

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-uploader`}>
      {label && <label className="text-sm font-medium text-gray-700 block">{label}</label>}

      {previewUrl ? (
        <div className="relative inline-block">
          <img
            src={previewUrl}
            alt="Aperçu"
            className={`${sizeClasses} object-cover rounded-lg border border-gray-200`}
            onError={(e) => { e.target.style.opacity = '0.4'; }}
          />
          {uploading && (
            <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-white" />
            </div>
          )}
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 shadow-md"
            data-testid={`${testIdPrefix}-remove-btn`}
            aria-label="Supprimer l'image"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => !uploading && inputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition ${
            dragOver ? 'border-[#0B6AB3] bg-blue-50' : 'border-gray-300 hover:border-[#0B6AB3] hover:bg-gray-50'
          } ${uploading ? 'pointer-events-none opacity-70' : ''}`}
          data-testid={`${testIdPrefix}-drop-zone`}
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-2 text-gray-600">
              <Loader2 className="w-8 h-8 animate-spin" />
              <span className="text-sm">Téléchargement…</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 text-gray-600">
              <ImageIcon className="w-10 h-10 text-gray-400" />
              <p className="text-sm">Glissez-déposez une image ou cliquez pour parcourir</p>
              <p className="text-xs text-gray-500">JPG, PNG, WebP, GIF (max {maxSizeMB} Mo)</p>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid={`${testIdPrefix}-file-input`}
        />
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid={`${testIdPrefix}-camera-input`}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`${testIdPrefix}-browse-btn`}
        >
          <Upload className="w-4 h-4 mr-2" />
          Parcourir
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => cameraRef.current?.click()}
          disabled={uploading}
          data-testid={`${testIdPrefix}-camera-btn`}
        >
          <Camera className="w-4 h-4 mr-2" />
          Caméra
        </Button>
      </div>

      <Input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="ou collez une URL d'image..."
        className="text-xs"
        data-testid={`${testIdPrefix}-url-input`}
      />
    </div>
  );
}

/**
 * Multi-image variant: same UX but adds to an array.
 */
export function MultiImageUploader({
  values = [],
  onChange,
  label = "Images",
  maxSizeMB = 10,
  testIdPrefix = 'multi-image',
}) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const [urlDraft, setUrlDraft] = useState('');

  const uploadFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Seules les images sont autorisées');
      return;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`Image trop volumineuse (max ${maxSizeMB} Mo)`);
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/admin/upload/image`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange([...values, res.data.url]);
      toast.success('Image ajoutée');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de téléchargement');
    } finally {
      setUploading(false);
    }
  };

  const handleFiles = async (fileList) => {
    if (!fileList) return;
    const files = Array.from(fileList);
    for (const file of files) {
      await uploadFile(file);
    }
  };

  const addUrl = () => {
    if (urlDraft.trim()) {
      onChange([...values, urlDraft.trim()]);
      setUrlDraft('');
    }
  };

  const removeImage = (index) => {
    onChange(values.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-uploader`}>
      {label && <label className="text-sm font-medium text-gray-700 block">{label}</label>}

      {values.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {values.map((img, idx) => (
            <div key={idx} className="relative w-24 h-24">
              <img
                src={resolveImageUrl(img)}
                alt=""
                className="w-full h-full object-cover rounded border"
              />
              <button
                type="button"
                onClick={() => removeImage(idx)}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-600"
                data-testid={`${testIdPrefix}-remove-${idx}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid={`${testIdPrefix}-file-input`}
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid={`${testIdPrefix}-camera-input`}
      />

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`${testIdPrefix}-browse-btn`}
        >
          {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
          Parcourir des images
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => cameraRef.current?.click()}
          disabled={uploading}
          data-testid={`${testIdPrefix}-camera-btn`}
        >
          <Camera className="w-4 h-4 mr-2" />
          Caméra
        </Button>
      </div>

      <div className="flex gap-2">
        <Input
          value={urlDraft}
          onChange={(e) => setUrlDraft(e.target.value)}
          placeholder="ou ajoutez une URL d'image..."
          className="text-xs"
          data-testid={`${testIdPrefix}-url-input`}
        />
        <Button type="button" variant="outline" size="sm" onClick={addUrl} data-testid={`${testIdPrefix}-add-url-btn`}>
          Ajouter
        </Button>
      </div>
    </div>
  );
}
